import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import type { components } from "@dolfin/api-admin";

type InvoiceActivityResponse = components["schemas"]["InvoiceActivityResponse"];

export interface ArActivity {
  loading: boolean;
  error: string | null;
  items: InvoiceActivityResponse[];
  refresh: () => Promise<void>;
}

/** Org-wide invoice activity, newest first; the "Recent activity" card is the first page. */
export function useArActivity(limit = 5): ArActivity {
  const { activeOrganisationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<InvoiceActivityResponse[]>([]);

  const fetchActivity = useCallback(async () => {
    if (!activeOrganisationId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await api.GET("/v1/invoices/audit-logs", {
        params: { query: { Limit: limit } },
      });

      if (res.error) {
        setError("Failed to load recent activity");
      }

      setItems(res.data?.data ?? []);
    } catch (e) {
      console.error("Failed to load recent activity:", e);
      setError("Failed to load recent activity");
    } finally {
      setLoading(false);
    }
  }, [activeOrganisationId, limit]);

  useEffect(() => {
    void fetchActivity();
  }, [fetchActivity]);

  return { loading, error, items, refresh: fetchActivity };
}
